/**
 * chrome-shell — Day 11+12+13 of the SimCity-look upgrade.
 *
 * Renders the disciplined frame around the map that matches the user's
 * mockups:
 *
 *   ┌────────────────────────────────────────────────────────┐
 *   │ TOP HEADER  logo · search  ·   wx  aqi  bell  avatar   │
 *   ├────────┬─────────────────────────────────┬─────────────┤
 *   │ LEFT   │                                 │  RIGHT      │
 *   │ RAIL   │                                 │  RAIL       │
 *   │        │           MAP                   │             │
 *   │ Local  │                                 │  Suburb     │
 *   │ Pulse  │                                 │  Intel      │
 *   │        │                                 │             │
 *   ├────────┴─────────────────────────────────┴─────────────┤
 *   │ BOTTOM NAV  Live · Pulse · Explore · Compare · Saved   │
 *   └────────────────────────────────────────────────────────┘
 *
 * Layout principles:
 *   - Chrome overlays the map; map gets a window in the center clear of
 *     the rails so verdicts + radar pulses are unobstructed.
 *   - All existing functionality is preserved: city toggle goes into top
 *     header, watchlist becomes bottom nav, search uses existing handler.
 *   - Mobile collapses both rails behind a single "More" button — only
 *     the top header (compact) + bottom nav (icon-only) + map remain.
 *
 * What this module does NOT do:
 *   - Replace any existing logic. Click handlers in the new chrome
 *     dispatch to existing components (suburbSearch, compare, schools
 *     chart, watchlist, etc.) so functionality stays identical.
 *   - Touch the verdict-card / school-chart / Decide-page
 *     overlays — those still mount above the chrome on demand.
 *
 * Wired event flow:
 *   - Clicking the city chip in top-header → toggles the existing city-
 *     toggle pill (which navigates to /app/?city=...)
 *   - Clicking a "Local Pulse" item → opens incident-card via the
 *     existing hyperlocal:incident-tap event channel
 *   - Clicking bottom-nav Compare → triggers existing compare flow
 *   - Clicking bottom-nav Watchlist → opens existing picks page
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyMap = any;

import { SUBTYPE_COLOR, BP_MOBILE } from "./tokens";
import { relativeAge } from "./freshness";
import { loadIncidents } from "../data/incidents-loader";

const SHELL_ID = "hyperlocal-shell";
const STYLE_ID = "hyperlocal-shell-styles";

interface InstallOpts {
  cityId: "hyd" | "blr" | "mel" | "syd";
  cityLabel: string;
  /** All verdict coords in the active city — used by the Map button to
   *  fitBounds() the entire city in one shot. lng,lat order. */
  points?: Array<[number, number]>;
}

// (City label dictionary removed — opts.cityLabel is now passed in
// directly from main-simple.ts so this hardcoded map became dead code.)

/* ------------------------------- STYLES ------------------------------- */

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
  /* SHELL — overlays the map, but leaves a centre window unobstructed
     so the user can read map content. Rails + nav are absolute-positioned
     within this wrap; pointer-events:none on the wrap itself so the map
     stays fully draggable through the gaps. */
  #${SHELL_ID} {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 6;
    color: #F4F2EE;
    font: 14px/1.4 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  }

  /* ------------------ TOP CHIPS (weather + AQI only) ------------------
     The full hyperlocâl long bar (logo + secondary search + bell + avatar)
     was retired 2026-05-07 — it duplicated the floating suburb-search pill.
     Only the per-suburb weather and AQI chips remain, floated top-right. */
  .es-top {
    position: absolute;
    top: 14px; right: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    background: transparent;
    border: 0;
    pointer-events: auto;
  }

  .es-chips { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; }
  .es-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 11px;
    background: rgba(20,20,24,0.6);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 999px;
    font-size: 12.5px;
    cursor: pointer;
    transition: background 140ms, border-color 140ms;
  }
  .es-chip:hover { background: rgba(30,30,36,0.85); border-color: rgba(255,211,126,0.40); }
  .es-chip-icon { font-size: 13px; }
  /* (.es-chip-aqi-* / .es-bell / .es-avatar CSS deleted 2026-06-11 — their
     elements were retired with the fabricated weather/AQI chips and the
     2026-05-07 header cleanup; the rules outlived the DOM by a month.) */

  /* The adopted city switcher (installCityToggle's pill, reparented into
     this slot by installChromeShell) — neutralise its free-floating inline
     position so the .es-top flex row owns layout. */
  .es-top #hyperlocal-city-toggle {
    position: static !important;
    top: auto !important;
    right: auto !important;
    z-index: auto !important;
  }

  /* ------------------ LEFT RAIL ------------------
     Anchored to the absolute bottom-left corner. Shows 5 incident cards
     by default; the list itself scrolls internally when there are more
     than 5 — the scrollbar IS the dropdown. The earlier chevron
     expand-collapse pattern was retired 2026-05-08 per user feedback
     ("only ~2.5 news visible, want 5 + scrollbar").
     Vertical budget: head ~70 + 5 cards × 78 + footer link ~30 + 24
     padding ≈ 514. We cap at 540 for safety; on shorter viewports the
     rail clamps to 70vh so it never eats the whole screen. */
  .es-left {
    position: absolute;
    bottom: 14px;
    left: 14px;
    width: 280px;
    max-height: min(540px, 70vh);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: rgba(15,15,18,0.86);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 14px;
    padding: 12px;
    pointer-events: auto;
    box-shadow: 0 14px 30px -10px rgba(0,0,0,0.55);
  }
  /* List scrolls internally — head + footer stay visible while the
     middle section gives way to a scrollbar past 5 items. */
  .es-pulse-list {
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: hidden;
    /* Always-on scrollbar styling so users see the affordance without
       hovering. Subtle so it doesn't fight the dark glass surface. */
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.20) transparent;
  }
  .es-pulse-list::-webkit-scrollbar { width: 6px; }
  .es-pulse-list::-webkit-scrollbar-track { background: transparent; }
  .es-pulse-list::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.18);
    border-radius: 999px;
  }
  .es-pulse-list::-webkit-scrollbar-thumb:hover {
    background: rgba(255,255,255,0.32);
  }
  .es-pulse-head {
    display: flex; align-items: center; gap: 8px;
    margin-bottom: 4px;
  }
  .es-pulse-title {
    font: 700 14px/1.2 system-ui;
    color: #F4F2EE;
  }
  .es-pulse-live {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 700;
    color: #FF4A4A;
    background: rgba(255,74,74,0.15);
    border: 1px solid rgba(255,74,74,0.30);
    border-radius: 4px;
    padding: 2px 6px;
    letter-spacing: 0.04em;
  }
  .es-pulse-live::before {
    content: ""; width: 6px; height: 6px; background: #FF4A4A;
    border-radius: 999px; animation: es-breathe 2.4s ease-in-out infinite;
  }
  /* A calm breath, not a blink — same cadence as the landing wordmark
     bullet. The badge itself only renders when real items exist. */
  @keyframes es-breathe { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
  @media (prefers-reduced-motion: reduce) {
    .es-pulse-live::before { animation: none; }
  }
  .es-pulse-sub {
    font-size: 12px;
    color: rgba(244,242,238,0.55);
  }
  .es-pulse-meta {
    font-size: 11px;
    color: rgba(244,242,238,0.40);
    margin: 6px 0 12px;
  }
  .es-pulse-list { display: flex; flex-direction: column; gap: 8px; }
  .es-pulse-item {
    display: flex; gap: 10px;
    padding: 10px 12px;
    background: rgba(20,20,24,0.6);
    border: 1px solid rgba(255,255,255,0.06);
    border-left: 3px solid var(--c, #FFD37E);
    border-radius: 8px;
    cursor: pointer;
    transition: background 140ms;
  }
  .es-pulse-item:hover { background: rgba(30,30,36,0.85); }
  .es-pulse-glyph {
    width: 28px; height: 28px;
    flex: 0 0 auto;
    border-radius: 999px;
    background: rgba(255,255,255,0.04);
    border: 1px solid var(--c, #FFD37E);
    color: var(--c, #FFD37E);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
  .es-pulse-text { flex: 1; min-width: 0; }
  .es-pulse-cat {
    font-size: 11px; font-weight: 700;
    color: var(--c, #FFD37E);
    letter-spacing: 0.04em;
    margin-bottom: 2px;
  }
  .es-pulse-headline {
    font-size: 12.5px;
    color: #F4F2EE;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .es-pulse-time {
    font-size: 10.5px;
    color: rgba(244,242,238,0.40);
    margin-top: 4px;
    line-height: 1.4;
  }
  /* Source link inside .es-pulse-time — visually subordinate but
     clickable. opens the article in a new tab; click bubble is
     stopped so the card's flyTo doesn't also fire. */
  .es-pulse-src {
    color: rgba(159,207,224,0.85);
    text-decoration: none;
  }
  .es-pulse-src:hover {
    color: #fff;
    text-decoration: underline;
  }
  .es-pulse-count {
    flex: 0 0 auto;
    align-self: flex-start;
    background: var(--c, #FFD37E);
    color: #1a1a1a;
    border-radius: 999px;
    font: 700 10px/16px system-ui;
    padding: 0 6px;
    min-width: 18px;
    text-align: center;
  }

  /* ------------------ BOTTOM NAV — Apple HIG dock ------------------
     Restructured 2026-05-07: the bar lost Pulse/Explore/Locate/Saved/
     Profile (per user — they didn't pull weight). What remains:
       [ Map ] · ( search ) · [ compare ] | [ filter ]
     Re-styled 2026-05-07 against Apple HIG (clarity / deference / depth):
       - Vibrancy material: blur + saturate so background colour shows
         through tinted, the way iOS dock + Safari address bar do it.
       - Hairline separators (1px, ~6% white) instead of full-saturation
         borders.
       - Search field uses the iOS Dark "tertiary system fill"
         (rgba(120,120,128,0.16)) instead of a tomato-red border that
         was fighting the pills.
       - Compare + Filter pills sit on transparent ground with a small
         flat tint, sentence-case labels, weight 590 (Apple Medium).
       - Active state uses iOS Dark systemRed (#FF453A) — quieter and
         on-brand for dark-mode UI than the previous #FF4A4A.
     Fonts use the SF Pro stack so the bar visually matches Safari /
     iOS Maps controls when the user is on Apple hardware. */
  .es-bottom {
    position: absolute;
    bottom: 14px; left: 50%;
    transform: translateX(-50%);
    background: rgba(28,28,30,0.72);
    backdrop-filter: saturate(180%) blur(20px);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 999px;
    padding: 5px;
    display: flex;
    align-items: center;
    gap: 6px;
    pointer-events: auto;
    box-shadow: 0 8px 32px -10px rgba(0,0,0,0.50), 0 1px 0 0 rgba(255,255,255,0.04) inset;
    font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, "Segoe UI", Roboto, sans-serif;
  }
  .es-bottom-left, .es-bottom-right {
    display: flex; align-items: center; gap: 4px;
    flex: 1 1 0;
  }
  .es-bottom-right { justify-content: flex-end; }
  .es-bottom-center {
    display: flex; align-items: center;
    flex: 0 0 auto;
  }
  /* All bar buttons share one shape: a row-flex pill with a monoline
     SF-Symbols-style glyph next to the label. Single tint policy —
     idle is iOS Dark secondaryLabel, hover lifts to primary, active
     uses iOS Dark systemRed (#FF453A) with a low-saturation tint
     ground so the active tab reads as the *only* coloured chip. */
  .es-nav-btn {
    background: transparent;
    border: 0;
    color: rgba(235,235,245,0.60); /* iOS Dark secondaryLabel */
    padding: 7px 11px;
    border-radius: 999px;
    cursor: pointer;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    font-weight: 510;
    letter-spacing: -0.01em;
    transition: background 140ms ease, color 140ms ease;
    white-space: nowrap;
  }
  .es-nav-btn:hover { background: rgba(255,255,255,0.06); color: rgba(235,235,245,0.95); }
  .es-nav-btn.active {
    /* Gold is the one interactive accent (design language §4). The old
       iOS systemRed active tab sat 5 RGB units from the LIVE/danger red —
       the selected Map tab glowed the same hue family as a crime alert. */
    color: var(--gold, #FFD37E);
    background: var(--gold-a12, rgba(255,211,126,0.10));
  }
  .es-nav-btn .es-nav-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    line-height: 1;
  }
  .es-nav-btn .es-nav-icon svg {
    width: 16px;
    height: 16px;
    display: block;
    stroke: currentColor;
  }
  /* Pulse rail toggle — desktop default is visible; .es-pulse-collapsed
     hides it. Mobile default (in @media block below) is hidden;
     .es-pulse-shown overrides that to surface the rail when the user
     taps Pulse. */
  .es-left.es-pulse-collapsed { display: none !important; }

  /* (The Picks tab, its caret, and the right-rail shortlist popover were
     deleted 2026-06-13 — picks live inside the Decide page now, the one
     opt-in buyer surface. docs/ONE_STORY.md.) */
  /* Map-controls slot — adopts the +/-/compass column from
     installMapControls and flattens it into a horizontal trio that fits
     the bar height. The original .hyperlocal-mc-root is fixed-positioned at
     right:14/bottom:14; once it's a child of .es-mc-slot we reset its
     positioning + flex-direction so the bar's flex layout owns it. */
  .es-mc-slot { display: inline-flex; align-items: center; gap: 4px; }
  .es-mc-slot #hyperlocal-map-controls,
  .es-mc-slot .hyperlocal-mc-root {
    position: static !important;
    right: auto !important;
    bottom: auto !important;
    flex-direction: row !important;
    gap: 4px !important;
    pointer-events: auto !important;
  }
  /* Map controls take the same chip language as everything else: 30px
     square, no shadow, transparent ground that reveals the bar's
     vibrancy. Only differs from text pills in that it's icon-only,
     so we use a soft 8px radius (same as iOS toolbar icon buttons)
     instead of the full 999px pill — signals "icon" vs "label". */
  .es-mc-slot .hyperlocal-mc-btn {
    width: 30px !important;
    height: 30px !important;
    border-radius: 8px !important;
    font-size: 16px !important;
    font-weight: 400 !important;
    box-shadow: none !important;
    background: transparent !important;
    border: 0 !important;
    color: rgba(235,235,245,0.75) !important;
  }
  .es-mc-slot .hyperlocal-mc-btn:hover {
    background: rgba(255,255,255,0.06) !important;
    color: rgba(235,235,245,0.95) !important;
  }
  .es-mc-slot .hyperlocal-mc-compass svg { width: 16px; height: 16px; }

  /* Slots inside the bottom bar that adopt the suburb search input and
     the Decide chip (reparented after their owning components install).
     The original components keep all their click handlers — we just
     relocate them into the chrome shell so the map stays uncluttered. */
  .es-search-slot, .es-filter-slot {
    display: inline-flex; align-items: center;
    flex: 0 0 auto;
  }
  /* Search field — fixed width. Earlier we expanded the slot from 240px
     to 460px on focus-within for a "fluid" Apple feel, but that growth
     pushed the right slot's Compare pill (width changes when its label
     becomes "Compare 2 areas") into the search box on narrow viewports.
     Holding a single stable width keeps the bar layout deterministic. */
  .es-search-slot {
    flex: 0 0 auto;
    width: 280px;
  }
  /* Both side slots are content-sized so neither can collapse below the
     width of its children. flex:1 1 0 on a side slot collapses it to its
     basis (0) inside the bar's shrink-to-fit container, which lets the
     Map button or Compare pill overflow into the centred search. */
  .es-bottom-left,
  .es-bottom-right { flex: 0 0 auto; }
  /* Hairline divider — kept in CSS for any future single-divider use,
     but no longer rendered in buildBottomNav. The bar relies on chip-
     family typography + a 6px breathing gap between the icon group
     (map-controls) and the action chips to signal grouping. */
  .es-divider {
    width: 1px;
    height: 18px;
    background: rgba(255,255,255,0.08);
    margin: 0 4px;
    flex: 0 0 auto;
  }
  /* Breathing gap that visually separates the icon-only zoom/locate
     cluster from the Decide chip. 6px feels like a soft section
     break without being a wall — replaces the previous hairline divider. */
  .es-mc-slot + .es-filter-slot { margin-left: 6px; }
  /* Reset the absolute positioning of the reparented controls so they
     flow inside the bottom bar like normal nav items. The wrap stays
     position:relative so its inner absolute-positioned dropdown
     (#hyperlocal-suburb-search-list) still anchors to the input — without
     this it would inherit the bottom-bar positioning context and
     stretch to the bar full width. */
  .es-bottom #hyperlocal-suburb-search {
    position: relative !important;
    top: auto !important; left: auto !important;
    transform: none !important;
    width: 100% !important;
    z-index: auto !important;
    pointer-events: auto !important;
  }
  /* Geo pill adopted into the dock on desktop (its fixed bottom-left home
     is occupied by the Local Pulse rail there). It keeps its own state
     styling (locating/located/denied); only the positioning is reset. */
  .es-bottom #hyperlocal-geo-pill {
    position: relative !important;
    bottom: auto !important; left: auto !important;
    z-index: auto !important;
  }
  /* iOS-style search field. Tertiary system fill + zero border at rest;
     focus brings up a 3px tint ring (no border colour change) — same
     pattern as Safari's URL bar focus. */
  .es-bottom #hyperlocal-suburb-search input {
    padding: 7px 14px !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    letter-spacing: -0.01em !important;
    color: rgba(235,235,245,0.95) !important;
    background: rgba(120,120,128,0.16) !important;
    border: 0 !important;
    border-radius: 999px !important;
    box-shadow: none !important;
    transition: background 160ms ease, box-shadow 160ms ease !important;
  }
  .es-bottom #hyperlocal-suburb-search input::placeholder {
    color: rgba(235,235,245,0.45) !important;
  }
  .es-bottom #hyperlocal-suburb-search input:hover {
    background: rgba(120,120,128,0.22) !important;
  }
  .es-bottom #hyperlocal-suburb-search input:focus {
    background: rgba(120,120,128,0.24) !important;
    box-shadow: 0 0 0 3px rgba(10,132,255,0.30) !important; /* iOS Dark systemBlue tint */
    outline: none !important;
  }
  /* Dropdown anchors above the input — slides UP from the bottom bar. */
  .es-bottom #hyperlocal-suburb-search-list {
    top: auto !important;
    bottom: calc(100% + 8px) !important;
  }
  /* Decide chip — the ONE buyer-suite entry in the dock. Neutral glass
     chip like the nav buttons (the bar reserves colour for active state);
     the open-state tint lives in main-simple's stylesheet keyed on
     body[data-mobile-filters="open"]. */
  .es-bottom #hyperlocal-filter-fab {
    position: static !important;
    transform: none !important;
    margin: 0 !important;
    padding: 7px 11px !important;
    background: transparent !important;
    border: 0 !important;
    color: rgba(235,235,245,0.85) !important;
    font-size: 12.5px !important;
    font-weight: 510 !important;
    letter-spacing: -0.01em !important;
    text-transform: none !important;
    border-radius: 999px !important;
    box-shadow: none !important;
    gap: 6px !important;
    height: auto !important;
    top: auto !important; right: auto !important; bottom: auto !important;
  }
  .es-bottom #hyperlocal-filter-fab:hover {
    background: rgba(255,255,255,0.06) !important;
    color: rgba(235,235,245,1) !important;
  }
  .es-bottom #hyperlocal-filter-fab span {
    text-transform: none !important;
    letter-spacing: -0.01em !important;
  }
  /* (Compare + school-chart dock chips deleted 2026-06-13 — both live
     inside the Decide page now. docs/ONE_STORY.md.) */

  /* ------------------ MOBILE ------------------
     One breakpoint for the whole product: BP_MOBILE (tokens.ts) covers
     narrow AND short (landscape-phone) viewports, so a rotated phone gets
     one coherent layout instead of a half-desktop hybrid. */
  @media ${BP_MOBILE} {
    .es-top { top: 8px; right: 8px; gap: 6px; }
    .es-chips .es-chip span:not(.es-chip-icon) { display: none; }
    .es-left { display: none; }
    /* Tapping the Pulse tab on mobile surfaces the rail as a top-anchored
       sheet. display:flex (not block) keeps the rail's internal flex
       column alive so .es-pulse-list scrolls — block froze the sheet at
       60vh with items past the fold unreachable. bottom:auto releases the
       base rule's bottom anchor so the sheet hangs from the top. */
    .es-left.es-pulse-shown {
      display: flex !important;
      top: 96px !important;
      bottom: auto !important;
      left: 8px !important;
      right: 8px !important;
      width: auto !important;
      max-width: none !important;
      max-height: min(60vh, 60dvh) !important;
    }
    .es-bottom {
      bottom: 10px;
      /* Home-indicator clearance — the dock is the thumb surface. */
      padding: 4px 4px calc(4px + env(safe-area-inset-bottom, 0px));
      left: 8px; right: 8px;
      transform: none;
      max-width: none;
      flex-wrap: wrap;
      justify-content: center;
    }
    .es-bottom-left, .es-bottom-right { flex: 0 0 auto; }
    .es-bottom-center { flex: 1 1 100%; order: -1; }
    /* Mobile reverts to icon-stacked-over-label tab style (iOS bottom
       tab bar pattern) — the row layout we use on desktop wastes
       horizontal real estate when the bar is full-width. */
    .es-nav-btn {
      flex-direction: column;
      gap: 1px;
      min-width: 50px;
      padding: 6px 8px;
      font-size: 10px;
    }
    .es-nav-btn .es-nav-icon,
    .es-nav-btn .es-nav-icon svg { width: 18px; height: 18px; }
    .es-search-slot { width: 100%; min-width: 0; }
    .es-search-slot:focus-within { width: 100%; }
    /* Touch targets: 30px mouse-sized chips become 40px on touch,
       and the dock search input gets 16px type (44px tall) so iOS
       doesn't zoom the page on focus. The +/− zoom buttons are desktop
       affordances — phones pinch — so only the compass keeps its dock
       seat; dropping them keeps the bar to two rows on a 390px phone
       (three rows ate 147px of map). */
    .es-mc-slot .hyperlocal-mc-btn {
      width: 40px !important;
      height: 40px !important;
      font-size: 18px !important;
    }
    .es-mc-slot .hyperlocal-mc-btn:not(.hyperlocal-mc-compass) {
      display: none !important;
    }
    .es-bottom #hyperlocal-suburb-search input {
      font-size: 16px !important;
      padding: 11px 14px !important;
    }
    .es-filter-slot { order: 99; }
    .es-divider { display: none; }
  }

  /* (Mobile bottom-sheet CSS deleted 2026-06-11 — showMobileSheet and the
     Pulse/Explore/Profile buttons that opened it were removed 2026-05-07;
     ~85 lines of styling outlived their DOM by a month.) */
  `;
  document.head.appendChild(s);
}

/* ----------------------- BUILD: TOP HEADER ----------------------- */

// Weather/AQI chips — DELETED 2026-06-11. They rendered "16°C Showers ·
// AQI 59 Moderate" from a deterministic hash of the suburb name: fabricated
// readings presented as fact, on the one product whose contract is "no
// fabricated data". If real weather/AQI feeds ever land, rebuild the chips
// against those feeds; never resurrect the hash. The header element stays
// (empty) so the .es-top layout slot and its CSS remain stable.
function buildTopHeader(_opts: InstallOpts): HTMLElement {
  const top = document.createElement("div");
  top.className = "es-top";
  return top;
}

/* ----------------------- BUILD: LEFT RAIL ----------------------- */

// One palette (tokens.SUBTYPE_COLOR); incident_fire is a rail-only alias
// subtype the worker emits that the canonical taxonomy doesn't carry yet.
const SUBTYPE_COLOR_LR: Record<string, string> = {
  ...SUBTYPE_COLOR,
  incident_fire: "#FF6A33",
};

const SUBTYPE_LABEL_LR: Record<string, string> = {
  crime_violent: "SAFETY ALERT",
  crime_property: "PROPERTY",
  crime_other: "POLICE",
  traffic_major: "TRAFFIC",
  traffic_routine: "TRAFFIC",
  construction_major: "CONSTRUCTION",
  construction_routine: "CONSTRUCTION",
  incident_fire: "FIRE",
  weather_severe: "WEATHER",
  weather_routine: "WEATHER",
  civic: "CIVIC",
  events: "EVENT",
  aqi: "AQI",
  other: "UPDATE",
};

const SUBTYPE_GLYPH_LR: Record<string, string> = {
  crime_violent: "‼",
  crime_property: "⚠",
  crime_other: "⚠",
  traffic_major: "🚗",
  traffic_routine: "🚗",
  construction_major: "🏗",
  construction_routine: "🏗",
  incident_fire: "🔥",
  weather_severe: "🌩",
  weather_routine: "🌧",
  civic: "🏛",
  events: "🎭",
  aqi: "🍃",
  other: "•",
};

/** One clock (freshness.relativeAge) — a missing or unparseable timestamp
 *  says so instead of rendering blank, the same "never silently fresh"
 *  rule the heartbeat enforces. */
function relativeTimeLR(iso?: string | null): string {
  if (!iso) return "time unverified";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "time unverified";
  return relativeAge(t, Date.now());
}

/** Escape text before interpolating into the rail's innerHTML. */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Scheme-checked href for the source link — only http(s) passes; anything
 *  else (javascript:, data:, malformed) yields null so no link renders. */
function safeRailHref(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.protocol === "https:" || u.protocol === "http:") return esc(u.href);
  } catch {
    /* malformed — no link */
  }
  return null;
}

/**
 * Pull a clean publisher hostname out of an incident URL when the
 * worker's `it.source` field is empty — falls back from "https://news.
 * google.com/rss/articles/..." → "news.google.com".
 */
function safeHostname(url: string | undefined): string {
  if (!url) return "";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function buildLeftRail(map: AnyMap, opts: InstallOpts): HTMLElement {
  const left = document.createElement("aside");
  left.className = "es-left";
  left.setAttribute("aria-label", "Local Pulse — what's happening near you");

  // Skeleton mounts SYNCHRONOUSLY — the dock and rail must never wait on
  // the network (design language §2: chrome mounts in its final position
  // or not yet). The LIVE badge is deliberately absent here: it is only
  // earned once real items render. The fetch below fills in place.
  //
  // 5-visible + scroll pattern: rail height accommodates ~5 cards; the
  // list inside scrolls when there are more (the always-on scrollbar
  // is the dropdown affordance now). The previous chevron expander was
  // retired 2026-05-08 per user feedback.
  left.innerHTML = `
    <div class="es-pulse-head">
      <span class="es-pulse-title">Local Pulse</span>
    </div>
    <div class="es-pulse-sub">What's happening near you</div>
    <div class="es-pulse-meta">Listening for signals…</div>
    <div class="es-pulse-list" id="es-pulse-list"></div>
  `;

  void fillLeftRail(left, map, opts);
  return left;
}

async function fillLeftRail(
  left: HTMLElement,
  map: AnyMap,
  opts: InstallOpts,
): Promise<void> {
  // Read through the shared incidents-loader — the ONE place incidents
  // load (one fetch, one cache, one dedup; the rail used to run its own
  // cache-busted fetch of raw items, so it double-fetched the feed every
  // boot and could show duplicates the map had collapsed). Limited to the
  // 8 latest by published_at for the rail's vertical fit.
  let items: any[] = [];
  let generatedAt: string | null = null;
  let fetchOk = false;
  try {
    const feed = await loadIncidents(opts.cityId);
    fetchOk = feed.ready;
    generatedAt = feed.generatedAt;
    const merged: any[] = [...feed.deduped, ...feed.nearbyVideos];
    merged.sort(
      (a, b) =>
        (Date.parse(b.published_at || "") || 0) -
        (Date.parse(a.published_at || "") || 0),
    );
    items = merged.slice(0, 8);
  } catch {
    /* fetchOk stays false — the meta line states it honestly below */
  }

  // Try to load enrichment for richer subtypes.
  let enrichment: Record<string, any> = {};
  try {
    const er = await fetch(
      `/api/incidents-enrichment-${opts.cityId}.json`,
      { cache: "no-store" },
    );
    if (er.ok) {
      const j = await er.json();
      enrichment = j?.enriched ?? {};
    }
  } catch {
    /* */
  }

  const metaEl = left.querySelector<HTMLDivElement>(".es-pulse-meta");
  const headEl = left.querySelector<HTMLDivElement>(".es-pulse-head");

  // Absence shown as absence: no blinking LIVE badge over an empty list,
  // no "0 updates · 0 updates". One calm true sentence.
  if (items.length === 0) {
    if (metaEl) {
      metaEl.textContent = fetchOk
        ? "No live updates right now"
        : "Couldn't reach the live feed";
    }
    return;
  }

  // The badge is earned: only a rail with real items is "live". Freshness
  // comes from the feed's own generated_at — never implied.
  headEl?.insertAdjacentHTML(
    "beforeend",
    `<span class="es-pulse-live">LIVE</span>`,
  );
  if (metaEl) {
    metaEl.textContent = `${items.length} updates · ${
      generatedAt ? relativeTimeLR(generatedAt) : "freshness unverified"
    }`;
  }

  // Normalize legacy / alias subtype strings the worker still emits to the
  // canonical taxonomy. Worker sends bare "fire" for structure fires; the
  // canonical subtype is "incident_fire" (added 2026-05-07 so fires stop
  // showing as 🌩 WEATHER on the bar). Aliased client-side until the worker
  // redeploys with the new taxonomy.
  const SUBTYPE_ALIAS_LR: Record<string, string> = {
    fire: "incident_fire",
  };

  const listEl = left.querySelector<HTMLDivElement>("#es-pulse-list");
  if (listEl && items.length > 0) {
    for (const it of items) {
      const enr = enrichment[it.id] || {};
      const rawSubtype = enr.subtype || it.subtype || "other";
      const subtype = SUBTYPE_ALIAS_LR[rawSubtype] || rawSubtype;
      const color = SUBTYPE_COLOR_LR[subtype] || SUBTYPE_COLOR_LR.other;
      const label = SUBTYPE_LABEL_LR[subtype] || SUBTYPE_LABEL_LR.other;
      const glyph = SUBTYPE_GLYPH_LR[subtype] || "•";
      const sev = enr.severity || it.severity || 1;

      const card = document.createElement("div");
      card.className = "es-pulse-item";
      card.style.setProperty("--c", color);
      card.dataset.id = it.id;
      // Source: prefer the worker's it.source field (e.g., "Deccan
      // Herald", "9News Australia"); fall back to the URL hostname.
      // Every interpolation below is escaped and the href scheme-checked —
      // the same contract the verdict/incident cards use (engine data is
      // trusted, but the rule is uniform across every signal surface).
      const srcName = esc((it.source || safeHostname(it.url) || "").slice(0, 40));
      const srcHref = safeRailHref(it.url);
      const place = esc(it.suburb_name || "city-wide");
      card.innerHTML = `
        <div class="es-pulse-glyph" aria-hidden="true">${glyph}</div>
        <div class="es-pulse-text">
          <div class="es-pulse-cat">${esc(label)}</div>
          <div class="es-pulse-headline">${esc(it.headline || "")}</div>
          <div class="es-pulse-time">
            ${esc(relativeTimeLR(it.published_at))} · ${place}${
              srcName
                ? ` · ${
                    srcHref
                      ? `<a class="es-pulse-src" href="${srcHref}" target="_blank" rel="noopener">${srcName}</a>`
                      : `<span class="es-pulse-src">${srcName}</span>`
                  }`
                : ""
            }
          </div>
        </div>
        ${sev >= 4 ? `<span class="es-pulse-count">${esc(String(sev))}</span>` : ""}
      `;
      // Click on the source link → open the article in a new tab and
      // STOP propagation so it doesn't also fly the map.
      const srcLink = card.querySelector<HTMLAnchorElement>(".es-pulse-src");
      srcLink?.addEventListener("click", (e) => e.stopPropagation());
      // Click anywhere else on the card → fly the map to the incident
      // coords AND open the floating incident-card overlay. Both happen
      // because the user's "take me where this is happening" intent
      // demands both: the map zooms in so they can SEE the location,
      // and the card surfaces the headline + source URL right next to
      // the marker.
      card.addEventListener("click", () => {
        try {
          if (
            map?.flyTo &&
            Array.isArray(it.coord) &&
            it.coord.length === 2 &&
            typeof it.coord[0] === "number" &&
            typeof it.coord[1] === "number" &&
            !it.is_city_anchored
          ) {
            map.flyTo({
              center: it.coord,
              zoom: 14.5,
              duration: 800, // one select move (camera grammar)
              essential: true,
            });
          }
        } catch {
          /* map disposed — fall through to event dispatch */
        }
        const ev = new CustomEvent("hyperlocal:incident-tap", {
          detail: {
            incident: it,
            scored: {
              raw: it,
              effective_subtype: subtype,
              effective_severity: sev,
              tier: "area",
            },
          },
          bubbles: true,
        });
        card.dispatchEvent(ev);
      });
      listEl.appendChild(card);
    }
  }
}

/* ----------------------- BUILD: BOTTOM NAV ----------------------- */

function buildBottomNav(map: AnyMap, points?: Array<[number, number]>): HTMLElement {
  const nav = document.createElement("nav");
  nav.className = "es-bottom";
  nav.setAttribute("aria-label", "Primary navigation");
  // Monoline SF-Symbols-style glyphs (Lucide-aligned). Stroke uses
  // currentColor so a single CSS rule controls all icon colour —
  // hover, active, disabled all "just work" via the parent's color
  // property, the way SF Symbols inherit text colour on iOS.
  // Replaced emoji icons (🗺 📈 ★ ▾) on 2026-05-08 — emoji rendered
  // at inconsistent weights and the colored 🗺 fought the systemRed
  // active tint.
  const iconMap = `<svg viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6L3 4v14l6 2 6-2 6 2V6l-6-2-6 2z"/><path d="M9 4v16"/><path d="M15 6v16"/></svg>`;
  const iconPulse = `<svg viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>`;
  // Three-group layout: left (Map + Pulse tabs), center (Search), right
  // (geo + icon controls + the ONE Decide chip). The dock is the truth
  // story's chrome — the entire buyer suite lives behind Decide
  // (docs/ONE_STORY.md). No dividers — chip family + uniform spacing
  // groups visually.
  nav.innerHTML = `
    <div class="es-bottom-left">
      <button class="es-nav-btn active" type="button" data-action="map" title="Show all suburbs in this city">
        <span class="es-nav-icon">${iconMap}</span><span>Map</span>
      </button>
      <button class="es-nav-btn" type="button" data-action="pulse" aria-expanded="true" aria-controls="hyperlocal-local-pulse" title="Local Pulse — what's happening near you">
        <span class="es-nav-icon">${iconPulse}</span><span>Pulse</span>
      </button>
    </div>
    <div class="es-bottom-center">
      <span class="es-search-slot" data-slot="search"></span>
    </div>
    <div class="es-bottom-right">
      <span class="es-geo-slot" data-slot="geo"></span>
      <span class="es-mc-slot" data-slot="map-controls"></span>
      <span class="es-filter-slot" data-slot="filter"></span>
    </div>
  `;

  // Wire Map / Pulse tabs. Single-active tab semantics like iOS Maps:
  // tapping Map restores the rail; tapping Pulse toggles it. The rail's
  // .es-pulse-collapsed / .es-pulse-shown classes drive the visibility
  // CSS in injectStyles().
  nav.addEventListener("click", (ev) => {
    const t = ev.target as HTMLElement;
    const btn = t.closest<HTMLButtonElement>("button");
    if (!btn) return;
    const action = btn.dataset.action;
    const setActive = (el: HTMLElement) => {
      nav.querySelectorAll(".es-nav-btn.active").forEach((x) => x.classList.remove("active"));
      el.classList.add("active");
    };
    const pulseBtn = nav.querySelector<HTMLButtonElement>('.es-nav-btn[data-action="pulse"]');
    const mapBtn = nav.querySelector<HTMLButtonElement>('.es-nav-btn[data-action="map"]');
    const pulseRail = document.querySelector<HTMLElement>(".es-left");
    if (action === "map") {
      fitMapToCity(map, points);
      setActive(btn);
      // Map view brings Pulse rail back (default desktop state).
      pulseRail?.classList.remove("es-pulse-collapsed");
      pulseBtn?.setAttribute("aria-expanded", "true");
    } else if (action === "pulse") {
      if (!pulseRail) return;
      // Desktop default is rail visible — toggle .es-pulse-collapsed to
      // hide. On mobile the rail is display:none via media query, and
      // .es-pulse-shown overrides that. Track a single "open" flag and
      // apply both classes so one toggle handles both viewports.
      const wasOpen = !pulseRail.classList.contains("es-pulse-collapsed")
        && (!isMobile() || pulseRail.classList.contains("es-pulse-shown"));
      if (wasOpen) {
        pulseRail.classList.add("es-pulse-collapsed");
        pulseRail.classList.remove("es-pulse-shown");
        btn.setAttribute("aria-expanded", "false");
        if (mapBtn) setActive(mapBtn);
      } else {
        pulseRail.classList.remove("es-pulse-collapsed");
        pulseRail.classList.add("es-pulse-shown");
        btn.setAttribute("aria-expanded", "true");
        setActive(btn);
      }
    }
  });

  return nav;
}

/**
 * "Zoom out to the absolute max" — fits all verdict points for the active
 * city into view, padded for the chrome (left rail + bottom bar + top
 * chips). Falls back to a no-op when the points array is empty so a city
 * with no data still works without throwing.
 */
function fitMapToCity(map: AnyMap, points?: Array<[number, number]>): void {
  if (!map?.fitBounds || !points || points.length === 0) return;
  let minLng = Infinity, maxLng = -Infinity;
  let minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of points) {
    if (typeof lng !== "number" || typeof lat !== "number") continue;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  if (!Number.isFinite(minLng)) return;
  const padding = isMobile()
    ? { top: 60, bottom: 100, left: 16, right: 16 }
    : { top: 80, bottom: 110, left: 320, right: 40 };
  try {
    map.fitBounds(
      [[minLng, minLat], [maxLng, maxLat]],
      { padding, duration: 900, essential: true, pitch: 0, bearing: 0 },
    );
  } catch {
    /* map disposed — silent */
  }
}

/* --------------------- MOBILE BOTTOM-SHEET HELPERS --------------------- */

function isMobile(): boolean {
  return (
    typeof window !== "undefined" &&
    (window.innerWidth <= 720 || window.innerHeight <= 500)
  );
}

/* showMobileSheet + renderSheetTab were removed 2026-05-07 along with
   the Pulse / Explore / Profile bottom-nav buttons that triggered them.
   The mobile bottom-sheet UI no longer has an entry point. */

/* ------------------------------- INSTALL ------------------------------- */

let _installed = false;

/**
 * Wait for an element with the given id to appear, then run `cb` once.
 * Used to reparent the suburb-search and suburb-filter controls into the
 * bottom bar — those install AFTER the chrome shell, so we observe.
 * Gives up after `timeoutMs` to avoid leaking observers if the element
 * never lands (e.g., the install was disabled by a feature flag).
 */
function whenIdAppears(
  id: string,
  cb: (el: HTMLElement) => void,
  timeoutMs = 8000,
): void {
  const existing = document.getElementById(id);
  if (existing) { cb(existing); return; }
  const obs = new MutationObserver(() => {
    const el = document.getElementById(id);
    if (el) {
      obs.disconnect();
      cb(el);
    }
  });
  obs.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => obs.disconnect(), timeoutMs);
}

function adoptIntoBottomBar(bottom: HTMLElement): void {
  whenIdAppears("hyperlocal-suburb-search", (el) => {
    const slot = bottom.querySelector<HTMLElement>('.es-search-slot');
    if (slot && el.parentElement !== slot) slot.appendChild(el);
  });
  // The Decide chip (legacy id #hyperlocal-filter-fab). Compare and
  // school-chart pills are NOT adopted — they live inside the Decide
  // page (docs/ONE_STORY.md).
  whenIdAppears("hyperlocal-filter-fab", (el) => {
    const slot = bottom.querySelector<HTMLElement>('.es-filter-slot');
    if (slot && el.parentElement !== slot) slot.appendChild(el);
  });
  // Map controls (zoom-in / zoom-out / compass) — installMapControls in
  // src/ui/map-controls.ts mounts a vertical column at right:14/bottom:14.
  // Reparent the whole root into the bottom bar; the .es-bottom-scoped CSS
  // in injectStyles flips it to row-flex and shrinks the buttons to fit
  // the bar height.
  whenIdAppears("hyperlocal-map-controls", (el) => {
    const slot = bottom.querySelector<HTMLElement>('.es-mc-slot');
    if (slot && el.parentElement !== slot) slot.appendChild(el);
  });
  // Geo pill — adopted into the dock on EVERY viewport (2026-06-13). It
  // used to stay free-floating bottom-left on mobile, where it sat on top
  // of the dock's two wrapped rows; the dock is the one bottom system now.
  whenIdAppears("hyperlocal-geo-pill", (el) => {
    const slot = bottom.querySelector<HTMLElement>('.es-geo-slot');
    if (slot && el.parentElement !== slot) slot.appendChild(el);
  });
}

export async function installChromeShell(
  map: AnyMap,
  opts: InstallOpts,
): Promise<void> {
  if (_installed) return;
  injectStyles();

  let shell = document.getElementById(SHELL_ID) as HTMLDivElement | null;
  if (!shell) {
    shell = document.createElement("div");
    shell.id = SHELL_ID;
    const container = map?.getContainer ? map.getContainer() : document.body;
    container.appendChild(shell);
  }

  // Build chrome pieces — ALL synchronous. The left rail mounts its
  // skeleton instantly and fills itself when the incidents fetch lands;
  // the dock must never wait on the network (it used to appear up to 15s
  // late on a hung API, with the legacy chrome visible underneath).
  const top = buildTopHeader(opts);
  const left = buildLeftRail(map, opts);
  const bottom = buildBottomNav(map, opts.points);

  shell.appendChild(top);
  shell.appendChild(left);
  shell.appendChild(bottom);

  // Adopt the city switcher into the top slot. installCityToggle builds it
  // free-floating at top:16/right:16; the .es-top scoped CSS neutralises
  // that inline position. Before this adoption the app had NO city
  // switcher at all — the override hid the pill on the promise of a
  // header re-mount that never happened.
  whenIdAppears("hyperlocal-city-toggle", (el) => {
    if (el.parentElement !== top) top.appendChild(el);
  });

  // Hide existing chrome that has been replaced (so we don't show two
  // search boxes, etc.). All hidden elements still exist in the DOM with
  // their click handlers — we just style:none them.
  injectChromeOverrides();

  // Reparent the suburb-search input and the Suburb Filter pill into the
  // bottom bar slots once their owning components install. Both
  // components install AFTER the chrome shell (see main-simple.ts ~5230)
  // so we observe rather than query immediately.
  adoptIntoBottomBar(bottom);

  _installed = true;
  console.log("[chrome-shell] ✓ installed for city=" + opts.cityId);
}

function injectChromeOverrides(): void {
  const id = "hyperlocal-shell-overrides";
  if (document.getElementById(id)) return;
  const s = document.createElement("style");
  s.id = id;
  // Hide the existing controls whose function moved into the shell.
  // Keep them in DOM (display:none) so click handlers still fire if the
  // shell forwards events to them (e.g., locate me, suburb search).
  s.textContent = `
    /* City toggle is ADOPTED into .es-top (see installChromeShell), not
       hidden — the old display:none here left the app with no city
       switcher at all. */
    /* Old home chip — keep it visible as a "back to landing" affordance */
    /* #home-chip stays */
    @media (min-width: 721px) {
      /* Near-me pill stays mobile-only — on desktop the Local Pulse rail
         IS the near-me surface. The geo pill is now VISIBLE on desktop:
         it is the only geolocation entry point since the dock's Locate
         button was retired 2026-05-07, and hiding it orphaned the area
         snapshot's "Near you" mode entirely. */
      #hyperlocal-nearme-pill { display: none !important; }
    }
  `;
  document.head.appendChild(s);
}

export function teardownChromeShell(): void {
  const shell = document.getElementById(SHELL_ID);
  if (shell) shell.remove();
  const overrides = document.getElementById("hyperlocal-shell-overrides");
  if (overrides) overrides.remove();
  _installed = false;
}
